import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { CONTACTS } from "@/data/content";
import { useLanguage, NAV_ITEMS } from "@/i18n";

function LangToggle({ className = "" }) {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid="lang-toggle" className={`flex items-center rounded-full border border-stone-300 ${className}`}>
      <button
        data-testid="lang-toggle-it"
        onClick={() => setLang("it")}
        aria-label="Italiano"
        className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 md:px-3 md:py-1.5 ${
          lang === "it" ? "bg-[#C89D66] text-[#0F1012]" : "text-stone-500 hover:text-stone-900"
        }`}
      >
        IT
      </button>
      <button
        data-testid="lang-toggle-en"
        onClick={() => setLang("en")}
        aria-label="English"
        className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 md:px-3 md:py-1.5 ${
          lang === "en" ? "bg-[#C89D66] text-[#0F1012]" : "text-stone-500 hover:text-stone-900"
        }`}
      >
        EN
      </button>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-stone-300/80 bg-[#F7F5F0]/85 py-3 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2">
          <img src="/logo-icon.png" alt="Edilcomplex2020" className="h-7 w-auto transition-transform duration-500 group-hover:scale-105 md:h-9" />
          <span className="flex items-baseline gap-1.5">
            <span className="font-serif text-[13px] font-semibold uppercase tracking-wide text-stone-900 min-[420px]:text-base md:text-xl">
              Edilcomplex
            </span>
            <span className="font-mono text-[9px] tracking-[0.12em] text-[#8F6B3C] md:text-xs">2020</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_ITEMS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.testid}-link`}
              className={({ isActive }) =>
                `link-underline font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "active text-[#8F6B3C]" : "text-stone-600 hover:text-stone-900"
                }`
              }
            >
              {t(link.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LangToggle />
          <a
            data-testid="nav-whatsapp-cta"
            href={CONTACTS.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 whitespace-nowrap border border-[#C89D66]/60 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-[#8F6B3C] transition-colors duration-300 hover:bg-[#C89D66] hover:text-[#0F1012]"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            {t("navCta")}
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LangToggle />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-stone-900"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-stone-300/80 bg-[#F7F5F0]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_ITEMS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`nav-mobile-${link.testid}-link`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl uppercase tracking-wide ${
                        isActive ? "text-[#8F6B3C]" : "text-stone-800"
                      }`
                    }
                  >
                    {t(link.key)}
                  </NavLink>
                </motion.div>
              ))}
              <a
                data-testid="nav-mobile-whatsapp-cta"
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex w-fit items-center gap-2 whitespace-nowrap border border-[#C89D66]/60 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-[#8F6B3C]"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {t("navCta")}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
